<template>
  <LineChart
    :chart-options="chartOptions"
    :chart-data="data"
    :plugins="plugins"
  />
</template>

<script setup lang="ts">
import { Chart as ChartJS, registerables } from "chart.js"
import { Line as LineChart } from "vue-chartjs"
import type {
  ChartData,
  Plugin,
  ChartOptions,
} from "chart.js"
import "chartjs-adapter-date-fns"
import { useCssVar } from "@vueuse/core"

const { formatToMerchantCurrencyString } = useCurrency()

ChartJS.defaults.font.family = window
  .getComputedStyle(document.documentElement)
  .getPropertyValue("font-family")
ChartJS.register(...registerables)
const props = defineProps<{
  id: string
  data: ChartData<"line", number[], Date>
}>()
const emit = defineEmits(["hover-datapoint"])
const colorPrimary = useCssVar("--color-primary")
const colorText = useCssVar("--color-on-background")

const plugins: Plugin<"line">[] = [
  {
    id: "crosshair",
    afterDatasetsDraw: (
      chart_: ChartJS<"line", number[], Date>
    ) => {
      const activeElements =
        chart_?.tooltip?.getActiveElements()
      if (activeElements && activeElements.length > 0) {
        const activePoint = activeElements[0]
        const context = chart_.ctx
        const bottomY = chart_.chartArea.bottom
        const x = activePoint.element.x
        const y = activePoint.element.y
        context.save()
        context.beginPath()
        context.moveTo(x, y)
        context.lineTo(x, bottomY)
        context.lineWidth = 2
        context.strokeStyle = `rgb(${colorPrimary.value} / 0.3)`
        context.stroke()
        context.restore()
      }
    },
  },
  {
    id: "hoverEmitIndex",
    afterDatasetDraw: (chart_) => {
      const activeElements =
        chart_?.tooltip?.getActiveElements()
      if (activeElements && activeElements.length > 0) {
        const activePoint = activeElements[0]
        emit("hover-datapoint", activePoint.index)
      } else {
        emit("hover-datapoint")
      }
    },
    afterEvent: (chart, { event }) => {
      if (event.native?.type === "touchend") {
        chart.tooltip?.setActiveElements([], { x: 0, y: 0 })
        chart.setActiveElements([])
        chart.update()
      }
    },
  },
]

const chartOptions: ChartOptions<"line"> = reactiveComputed(
  () => ({
    maintainAspectRatio: false,
    animation: false,
    responsive: true,
    plugins: {
      tooltip: {
        enabled: false,
        mode: "nearest",
        intersect: false,
        axis: "x",
      },
      legend: {
        display: false,
      },
    },
    events: [
      "mousemove",
      "mouseout",
      "click",
      "touchstart",
      "touchmove",
      "touchend",
    ],
    // onHover: (...params) => emit("hover-datapoint", params),
    interaction: {
      mode: "nearest",
      intersect: false,
      axis: "x",
    },
    scales: {
      x: {
        stacked: true,
        type: "timeseries",
        time: {
          displayFormats: {
            month: "MMM",
          },
        },
        ticks: {
          font: {
            size: 12,
          },
          textStrokeColor: `rgb(${colorText.value} / 0.7)`,
          minRotation: 0,
          maxRotation: 0,
          padding: 8,
          align: "start",
          autoSkipPadding: 8,
          includeBounds: false,
        },
        grid: {
          color: `rgb(${colorText.value} / 0.05)`,
          drawTicks: false,
        },
      },
      yAxes: {
        suggestedMax: 10000,
        title: {
          display: false,
        },
        grid: {
          display: false,
          drawTicks: false,
        },
        ticks: {
          align: "center",
          // align: "end",
          maxTicksLimit: 3,
          mirror: true,
          font: {
            size: 12,
          },
          callback: (value) =>
            value !== 0 && !Number.isNaN(Number(value))
              ? formatToMerchantCurrencyString(
                  Number(value),
                  {
                    compact: true,
                  }
                )
              : "",
        },
        stacked: true,
        beginAtZero: true,
      },
    },
  })
)
</script>
